import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useDisconnectAccountFromIntegrationMutation,
	useGetExternalIntegrationQuery,
	usePostIntegrationApiKeyAuthMutation,
} from '../../../../queries';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../components-v2/Button';
import { Field, FieldError, FieldLabel } from '../../../components-v2/Field';
import { Input } from '../../../components-v2/Input';
import { THEME } from '../../../components-v2/Theme/theme';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

const disconnectModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Disconnect',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

enum TabValues {
	Configure = 'configure',
	Manage = 'manage',
}

export interface IApiKeyIntegrationPageProps {
	provider: Api.IntegrationProvider;
	integrationType: Api.ExternalIntegrationType;
	displayName: string;
	logo: string;
	logoAlt: string;
	description: string;
	/** Title case, e.g. "API Key". */
	credentialLabel: string;
	/** Sentence case, e.g. "API key". */
	credentialNoun: string;
	connectPrompt: string;
	manageConnectedText: string;
	manageNotConnectedText: string;
	disconnectTitle: string;
	onConnected?: () => void;
}

export function ApiKeyIntegrationPage({
	provider,
	integrationType,
	displayName,
	logo,
	logoAlt,
	description,
	credentialLabel,
	credentialNoun,
	connectPrompt,
	manageConnectedText,
	manageNotConnectedText,
	disconnectTitle,
	onConnected,
}: IApiKeyIntegrationPageProps) {
	const [credential, setCredential] = React.useState('');
	const [isEditing, setIsEditing] = React.useState(false);
	const [isDisconnectModalOpen, setIsDisconnectModalOpen] = React.useState(false);
	const history = useHistory();
	const userSession = useUserSession();
	const adminUserSession = userSession as AdminUserSessionContext;
	const params = useParams<{ tab?: string }>();
	const impersonationContext = userSession.impersonationContext;
	const inputId = `${provider.toLowerCase()}-credential`;
	const errorId = `${inputId}-error`;

	const authMutation = usePostIntegrationApiKeyAuthMutation({
		impersonationContext,
		provider,
		onSuccess: () => {
			onConnected?.();
			invalidateIntegrationQuery({ impersonationContext, provider });
			toast.success(`Successfully connected to ${displayName}.`);
			setCredential('');
			setIsEditing(false);
		},
	});

	const disconnectMutation = useDisconnectAccountFromIntegrationMutation({
		impersonationContext,
		onError: error => {
			toast.error(error.systemMessage || `Failed to disconnect ${displayName}. Please try again.`);
		},
		onSuccess: () => {
			invalidateIntegrationQuery({ impersonationContext, provider });
			toast.success(`${displayName} has been disconnected.`);
		},
	});

	const onDisconnectModalRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		setIsDisconnectModalOpen(false);
		if (result?.isDestructive && !canceled) {
			disconnectMutation.mutate(integrationType);
		}
	};

	const integrationQuery = useGetExternalIntegrationQuery({ impersonationContext, provider });

	const configuration = integrationQuery.data?.configuration;
	const isConnected = !!configuration?.accountConnection?.isConnected;
	const needsConfiguration = configuration?.needsConfiguration !== false;

	const changeTab = React.useCallback(
		(value: string) => {
			history.replace({
				...history.location,
				pathname: `/integrations/${provider}/${value}`,
			});
		},
		[history, provider]
	);

	const canConfigure = adminUserSession.isAdmin;
	const allowedTabs = canConfigure ? [TabValues.Configure, TabValues.Manage] : [TabValues.Manage];
	const defaultTab = needsConfiguration && canConfigure ? TabValues.Configure : TabValues.Manage;
	const activeTab = allowedTabs.find(tab => tab === params.tab) ?? defaultTab;
	const urlHasValidTab = activeTab === params.tab;

	React.useEffect(() => {
		if (!integrationQuery.data || urlHasValidTab) {
			return;
		}

		changeTab(defaultTab);
	}, [changeTab, defaultTab, integrationQuery.data, urlHasValidTab]);

	const onConnect = () => {
		if (!credential.trim()) {
			toast.error(`Please enter your ${displayName} ${credentialNoun}.`);
			return;
		}
		authMutation.mutate({ apiKey: credential.trim() });
	};

	if (integrationQuery.isLoading) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	const hasAuthError = !!authMutation.error;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(styleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={logo} alt={logoAlt} />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>{description}</p>

			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={activeTab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					{canConfigure ? (
						<Tabs.Trigger
							value={TabValues.Configure}
							className={`tab-view-tabbar-item ${
								activeTab === TabValues.Configure ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<SuccessCheckIcon
								fillColor={needsConfiguration ? THEME.colors.icon.disabled : undefined}
								ringColor={needsConfiguration ? THEME.colors.background.main : undefined}
							/>
							<span>Configure</span>
						</Tabs.Trigger>
					) : null}

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${
							activeTab === TabValues.Manage ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
						} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Configure} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						{needsConfiguration || isEditing ? (
							<>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									{needsConfiguration
										? connectPrompt
										: `Enter a new ${credentialNoun} to update your ${displayName} connection.`}
								</p>
								<div className={css(styleSheet.connectForm)}>
									<Field styles={[styleSheet.credentialField]}>
										<FieldLabel htmlFor={inputId}>{credentialLabel}</FieldLabel>
										<div className={css(styleSheet.credentialInputRow)}>
											<Input
												autoComplete='off'
												autoFocus={true}
												id={inputId}
												onChange={ev => setCredential(ev.target.value)}
												type='password'
												value={credential}
												styles={[styleSheet.credentialInput]}
												aria-invalid={hasAuthError}
												aria-describedby={hasAuthError ? errorId : undefined}
											/>
											{hasAuthError && <DangerIcon backgroundColor={THEME.colors.background.light} />}
										</div>
										{hasAuthError ? (
											<FieldError id={errorId}>
												{authMutation.error?.systemMessage ||
													`Unable to verify this ${credentialNoun}. Please try again.`}
											</FieldError>
										) : null}
									</Field>
									<div className={css(bs.horizontalStack)}>
										<Button isLoading={authMutation.isPending} onClick={onConnect} variant='primary'>
											{needsConfiguration ? 'Connect' : 'Save'}
										</Button>
										{isEditing ? (
											<Button
												disabled={authMutation.isPending}
												onClick={() => {
													setIsEditing(false);
													setCredential('');
													authMutation.reset();
												}}
												variant='outline'
											>
												Cancel
											</Button>
										) : null}
									</div>
								</div>
							</>
						) : (
							<>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									{`Your ${displayName} account is connected and configured to sync with Levitate.`}
								</p>
								<div className={css(bs.horizontalStack)}>
									<Button disabled={disconnectMutation.isPending} onClick={() => setIsEditing(true)} variant='primary'>
										{`Update ${credentialLabel}`}
									</Button>
									<Button
										isLoading={disconnectMutation.isPending}
										onClick={() => setIsDisconnectModalOpen(true)}
										variant='outline'
									>
										Disconnect
									</Button>
								</div>
							</>
						)}
					</div>
				</Tabs.Content>

				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							{isConnected ? manageConnectedText : manageNotConnectedText}
						</p>
						<Button asChild={true}>
							<Link to='/people'>Contacts</Link>
						</Button>
					</div>
				</Tabs.Content>
			</Tabs.Root>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isDisconnectModalOpen,
					onRequestClose: onDisconnectModalRequestClose,
				}}
				options={disconnectModalOptions}
				title={disconnectTitle}
			/>
		</div>
	);
}
